import React from 'react';
import type { Shopping } from '../types';

// From context-chicago/fuelling-research.md §1, §2 and §7. Quantities cover Thu 8–Sun 11 for a 70 kg runner
// with ~25% buffer.
export const chicagoShopping: Shopping = {
  lede: 'Three load days + race morning · low-FODMAP · lactose OK · cook dinners at the loft. US brands picked to avoid HFCS, agave, honey, inulin and garlic/onion powder.',
  stores: [
    {
      name: 'Jewel-Osco Woodlawn',
      address: '6014 S Cottage Grove Ave',
      walk: '~5 min walk',
      hours: '6 am–10 pm daily',
      bestFor: 'Main shop — rice, GF pasta, Gatorade, Lactaid, tuna, maple, jam, fruit, chicken.',
    },
    {
      name: 'Whole Foods Hyde Park',
      address: '5118 S Lake Park Ave',
      walk: '~10 min drive',
      hours: '8 am–10 pm daily',
      bestFor: 'Specialty run — Canyon Bakehouse Country White bread, Fody garlic oil, Bonne Maman, Wild Planet tuna.',
    },
  ],
  sections: [
    {
      title: 'Bring from Australia',
      icon: 'supplements',
      accent: true,
      wide: true,
      items: [
        {
          id: 'au-pf30',
          item: 'Precision PF 30 gels',
          detail: '8 for race day (2 caf) + spares · no Chicago stockist found · checked bag, a few in carry-on',
          qty: '× 10',
        },
        {
          id: 'au-pf-carb',
          item: 'PF Carb & Electrolyte Drink Mix',
          detail: '2 scoops / 500 mL = 30 g CHO + 500 mg Na · race breakfast, car-to-start bottle, rescue, soft-flask refills (pre-measure into bags)',
          qty: '1 bag (15 serves)',
        },
        {
          id: 'au-beet',
          item: 'Beet It Sport Nitrate 400 shots',
          detail: 'Already have enough — pack them · Mon 5 and Tue 6 doses taken at home',
          qty: '5 + 2 spare',
        },
        {
          id: 'au-nomio',
          item: 'Nomio shots',
          detail: 'Already have enough — pack them · 3-day preload from Thu 8 AM',
          qty: '6 + 1 spare',
        },
        { id: 'au-ph', item: 'Precision PH electrolyte tabs', detail: '500 mg Na · pre-race + load days', qty: '1 tube' },
        {
          id: 'au-declare',
          item: 'Declare all food at US customs',
          detail: 'Packaged gels, powders and shots are fine when declared · no meat · up to US$1,000 fine if undeclared',
          qty: '—',
        },
      ],
    },
    {
      title: 'Gear',
      icon: 'gear',
      items: [
        {
          id: 'kitchen-scales',
          item: 'Kitchen scales',
          detail: 'For weighing carbs, gels, and portions during fuelling',
          qty: '× 1',
        },
        {
          id: 'weight-scales',
          item: 'Weight scales',
          detail: 'Daily weigh-in during loading week',
          qty: '× 1',
        },
        {
          id: 'travel-adaptor',
          item: 'Travel adaptor',
          detail: 'AU → US Type A/B plug — for phone, watch, and chargers',
          qty: '× 1',
        },
      ],
    },
    {
      title: 'No-cook protein',
      icon: 'protein',
      accent: true,
      items: [
        {
          id: 'chicken',
          item: 'Plain raw chicken breast',
          detail: 'Cook at the loft · avoid rotisserie and seasoned strips (garlic/onion powder)',
          qty: '~700 g',
        },
        {
          id: 'tuna',
          item: 'Wild Planet Skipjack tuna',
          detail: 'Ingredients: tuna + sea salt only (Whole Foods) · StarKist can list vegetable broth',
          qty: '× 4',
        },
      ],
    },
    {
      title: 'Carb staples',
      icon: 'carbs',
      items: [
        {
          id: 'gf-bread',
          item: 'Canyon Bakehouse Country White',
          detail: 'NOT Mountain White (agave) · 13 g CHO, <1 g fibre per slice · Whole Foods Hyde Park',
          qty: '3 loaves',
        },
        { id: 'rice', item: 'Jasmine rice', detail: 'Any plain · 2 lb bag', qty: '907 g' },
        {
          id: 'gf-pasta',
          item: 'Barilla Gluten Free',
          detail: 'Corn + rice, lower fibre than brown-rice pastas · check label',
          qty: '2 × 340 g',
        },
        { id: 'rice-cakes', item: 'Rice cakes', detail: 'Quaker or Lundberg Lightly Salted', qty: '2 packs' },
        {
          id: 'rice-bars',
          item: "Kellogg's Rice Krispies Treats",
          detail: 'Original · US label may list corn syrup + fructose — fine in these amounts',
          qty: '1 box',
        },
      ],
    },
    {
      title: 'Fruit',
      icon: 'fruit',
      items: [
        { id: 'bananas', item: 'Firm bananas', detail: 'Slightly green — ripe bananas are higher FODMAP', qty: '× 8' },
        { id: 'strawberries', item: 'Strawberries', qty: '1 lb' },
        { id: 'oranges', item: 'Oranges', qty: '× 4' },
        { id: 'kiwi', item: 'Kiwi', qty: '× 4' },
      ],
    },
    {
      title: 'Condiments & flavour',
      icon: 'condiments',
      items: [
        { id: 'jam', item: 'Bonne Maman Strawberry Preserves', detail: 'Avoid HFCS jams (Smucker’s)', qty: '1 jar' },
        { id: 'maple', item: '100% pure maple syrup', detail: '"Pancake syrup" is corn syrup', qty: '12.5 oz' },
        { id: 'garlic-oil', item: 'Fody Garlic-Infused Olive Oil', detail: 'Whole Foods Hyde Park or Amazon · certified low-FODMAP', qty: '250 mL' },
        { id: 'soy', item: 'Kikkoman or San-J Tamari', qty: 'small bottle' },
        { id: 'passata', item: 'Plain passata', detail: 'Tomatoes + salt only — no onion or garlic', qty: '1 jar' },
        { id: 'salt', item: 'Salt', qty: '1 shaker' },
      ],
    },
    {
      title: 'Drinks',
      icon: 'drinks',
      items: [
        {
          id: 'gatorade',
          item: 'Gatorade Thirst Quencher',
          detail: 'Classic — not Zero or G2 · avoid Powerade US (HFCS)',
          qty: '8 × 20 oz',
        },
        { id: 'milk', item: 'Lactaid milk', detail: 'More carbs per cup than Fairlife', qty: '1 quart' },
        { id: 'oj', item: '100% orange juice', detail: 'Small bottles, ~125 mL serves', qty: '× 3' },
        { id: 'coffee', item: 'Coffee', detail: 'Check the loft has a coffee maker', qty: '—' },
        { id: 'water', item: 'Bottled water', qty: 'as needed' },
      ],
    },
  ],
  footnote: (
    <>
      <strong>Buy order:</strong> Jewel-Osco on arrival night (Wed 7, open to 10 pm) · Whole Foods Hyde Park on Thu 8
      evening for the bread, oil and tuna · fruit top-up Fri 9 · everything for race breakfast by Sat 10 — nothing near
      the loft is open before 6 am Sunday.
    </>
  ),
};
